// v0.6.10 — acknowledge several Family B rows with ONE note.
//
// Operator-reported: a group of 21 rows that were all the same
// situation (GB→EU Art 44 service, no buyer VAT number, all routed
// EXPORT) required 21 separate notes. Typing the same sentence 21
// times does not make an audit trail better — it makes a mechanical
// act look like 21 deliberate ones.
//
// WHY THIS IS ALLOWED. routes_review_ack's Family B guard refuses
// exactly one thing: acknowledging a group WITHOUT naming its rows
// ("bulk-acknowledge (omitting txn_ids) isn't allowed for this flag
// type"). Its own comment says Family B "accept acknowledgements
// provided the caller supplies explicit txn_ids". The rule is about
// not waving through a group you haven't looked at — not about how
// many you may decide at once. This sends explicit, operator-selected
// ids, so it satisfies the rule rather than working around it.
//
// The selection is therefore deliberate and visible: nothing is
// pre-selected, and the count being acknowledged is shown on the
// button itself.
//
// A2-528 (operator, 2026-10-25): "why to bulk select i need to go to
// bottom of list, its not intuitive". The bar stays BELOW the rows (A1-76:
// read the rows, then decide) but sticks to the bottom of the screen while
// its card is on screen, so it is never a scroll away. Every bulk bar uses
// STICKY_BAR; each sticks only within its own card.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { presetsFor } from "../../lib/reviewReasonPresets";

// A2-578 (operator 2026-10-27): at the TOP of its group, sticky under the app header.
export const STICKY_BAR = "sticky top-16 z-20 shadow-lg";

// A2-532 (operator 2026-10-25: "options still the same?") — where the card's
// real answer is a decision (a rate category, a credit note's channel), the
// card passes it as `decision`: it is offered for the ticked rows first, and
// "keep them as they are" becomes the secondary choice it is.
export const FamilyBBulkResolver = ({
  flagType, calcId, selected, total, onSelectAll, onClear, onResolved, decision = null, groupTotal = null,
  allRowsChoice = false,
  // A2-582: whether the server offers 'keep as it is' on these rows (g.bulk_answers),
  // in place of a hand list of flags it must not be offered for.
  keepOffered = true,
  // A2-582: the ticked rows keep may be sent for — rows ticked for another
  // answer (a country, a channel) are not sent with "keep".
  keepSelected = null,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const presets = presetsFor(flagType);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const n = selected.length;
  const keepIds = keepSelected || selected;
  const kn = keepIds.length;
  // A2-578: `total` is the rows on THIS page; the group can hold more.
  const group = Math.max(groupTotal || 0, total);
  const morePages = group > total;
  const cleaned = reason.trim();
  const valid = cleaned.length >= 10
    && !/<[A-Z_]+>/.test(cleaned)
    && !/[<>]/.test(cleaned);

  const disabledReason = (() => {
    if (valid || busy) return null;
    // v0.6.10 — no hint for "nothing chosen yet": the dropdown right
    // above already reads "Choose a reason…", and repeating it under a
    // greyed button is the kind of narration that makes a small panel
    // feel like a document.
    if (!cleaned) return null;
    if (cleaned.length < 10) return t("ui.review_grouped.row_resolve_need_more", {
      defaultValue: "{{n}} more character(s) needed.",
      n: 10 - cleaned.length,
    });
    return t("ui.review_grouped.row_resolve_no_brackets", {
      defaultValue: "Remove the < > characters.",
    });
  })();

  const submit = async () => {
    if (!valid || busy || !kn) return;
    setBusy(true);
    setErr(null);
    try {
      // ONE request carrying every selected id — the endpoint has
      // always taken a list; only the UI sent them one at a time.
      await authClient.post(`/runs/calc/${calcId}/review-acknowledge`, {
        flag_type: flagType,
        txn_ids: keepIds,
        reason: cleaned,
      });
      setOpen(false);
      setReason("");
      setPreset("");
      if (onClear) onClear();
      toast.success(
        t("ui.review_grouped.bulk_b_done", {
          defaultValue: "{{n}} rows acknowledged", n: kn,
        }),
        { description: t("ui.review_grouped.bulk_b_done_desc", {
          defaultValue: "Recorded in the audit trail.",
        }) },
      );
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(t("ui.review_grouped.row_resolve_failed", {
        defaultValue: "Could not acknowledge rows",
      }), { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`rg-bulkb-bar-${flagType}`}
      className={`${STICKY_BAR} mb-3 rounded-lg border-l-4 border bg-white dark:bg-slate-900 ${
        n > 0
          ? "border-l-emerald-500 border-emerald-200 dark:border-emerald-900"
          : "border-l-slate-300 dark:border-l-slate-600 border-slate-200 dark:border-slate-700"
      }`}
    >
      <div className="px-3 py-2 flex items-start gap-3 flex-wrap">
        <span
          className={`text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${
            n > 0
              ? "bg-emerald-600 text-white"
              : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
          }`}
        >
          {t("ui.review_grouped.bulk_b_pill", {
            defaultValue: "Decide together",
          })}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-900 dark:text-slate-100">
            {n > 0
              ? t("ui.review_grouped.bulk_b_headline_some", {
                defaultValue: "{{n}} of {{total}} selected — one reason covers all.",
                n,
                total: group,
              })
              : t("ui.review_grouped.bulk_b_headline_none", {
                defaultValue: "Tick rows, or select all {{page}} on this page, to decide them together.",
                page: total,
                total: group,
              })}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
            {decision
              ? t("ui.review_grouped.bulk_b_subtext_decision", {
                defaultValue:
                  "Fix the ticked rows below. 'Keep as they are' only "
                  + "records that you checked them; it changes nothing.",
              })
              : t("ui.review_grouped.bulk_b_subtext", {
                defaultValue:
                  "Nothing is re-taxed or re-routed — each row keeps the "
                  + "treatment in its Route. This only records that you "
                  + "checked them.",
              })}
          </div>
          {morePages && (
            <div data-testid={`rg-bulkb-more-${flagType}`}
                 className="text-xs font-semibold text-slate-800 dark:text-slate-100 mt-1">
              {/* A1-107 (operator 2026-11-02): it named a 'for all N rows' choice
                  the refund-channel card does not have - only where one is drawn */}
              {/* A1-107 (1b): wherever the card draws an all-rows control
                  (the rate pick, leave out, a channel), with or without a
                  ticked-rows decision in this bar */}
              {allRowsChoice
                ? t("ui.review_grouped.bulk_b_more_decision", {
                  defaultValue: "This page shows {{page}} of the {{total}} rows. To answer all {{total}} at once, use the 'for all {{total}} rows' choice at the top of this group.",
                  page: total, total: group })
                : t("ui.review_grouped.bulk_b_more", {
                  defaultValue: "This page shows {{page}} of the {{total}} rows; the next page holds the rest.",
                  page: total, total: group })}
            </div>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <button
              type="button"
              data-testid={`rg-bulkb-selectall-${flagType}`}
              onClick={onSelectAll}
              className="text-[10px] tabular-nums underline text-teal-700 dark:text-teal-300"
            >
              {t("ui.review_grouped.bulk_b_select_all", {
                defaultValue: "Select all {{page}} on this page",
                page: total,
              })}
            </button>
            {n > 0 && (
              <button
                type="button"
                data-testid={`rg-bulkb-clear-${flagType}`}
                onClick={onClear}
                className="text-[10px] tabular-nums underline text-slate-500 dark:text-slate-400"
              >
                {t("ui.review_grouped.bulk_b_clear", { defaultValue: "Clear" })}
              </button>
            )}
          </div>
        </div>
        {keepOffered && (
        <Popover
          open={open}
          onOpenChange={(o) => { setOpen(o); if (!o) { setReason(""); setPreset(""); setErr(null); } }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`rg-bulkb-open-${flagType}`}
              disabled={!kn}
              className={decision
                ? "shrink-0 text-xs underline text-slate-600 dark:text-slate-300 disabled:no-underline disabled:text-slate-400 disabled:cursor-not-allowed"
                : "shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-500 disabled:cursor-not-allowed"}
            >
              {decision && kn > 0
                ? t("ui.review_grouped.bulk_b_btn_keep", { defaultValue: "or keep {{n}} as they are", n: kn })
                : kn > 0
                ? t("ui.review_grouped.bulk_b_btn", {
                  defaultValue: "Acknowledge {{n}} selected", n: kn,
                })
                : t("ui.review_grouped.bulk_b_btn_none", {
                  defaultValue: "Acknowledge selected",
                })}
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid={`rg-bulkb-popover-${flagType}`}
            className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
          >
            <div className="text-xs font-bold text-slate-900 dark:text-slate-100">
              {t("ui.review_grouped.bulk_b_heading", {
                defaultValue: "Keep these {{n}} rows as they are", n: kn,
              })}
            </div>
            <div className="text-[11px] leading-snug text-slate-600 dark:text-slate-300">
              {t("ui.review_grouped.row_resolve_effect")}
            </div>
              {/* v0.6.10 — pick, don't write. See
                  lib/reviewReasonPresets for why "Other" survives. */}
              <select
                data-testid={`rg-bulkb-preset-${flagType}`}
                value={preset}
                onChange={(e) => {
                  setPreset(e.target.value);
                  setReason(e.target.value);
                }}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-[11px]"
              >
                <option value="">
                  {t("ui.review_grouped.reason_pick", {
                    defaultValue: "Choose a reason…",
                  })}
                </option>
                {presets.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            {err && (
              <div
                data-testid={`rg-bulkb-error-${flagType}`}
                className="text-[10px] text-red-700 dark:text-red-300"
              >
                {err}
              </div>
            )}
            {disabledReason && (
              <div className="text-[10px] text-amber-700 dark:text-amber-300">
                {disabledReason}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid={`rg-bulkb-cancel-${flagType}`}
                onClick={() => { setOpen(false); setReason(""); setPreset(""); setErr(null); }}
                className="text-[10px] underline text-slate-600 dark:text-slate-300"
              >
                {t("ui.review_grouped.row_resolve_cancel")}
              </button>
              <button
                type="button"
                data-testid={`rg-bulkb-submit-${flagType}`}
                data-resolution-class="recordkeeping"
                data-resolution-endpoint="POST /api/runs/calc/{calc_id}/review-acknowledge"
                disabled={busy || !valid}
                onClick={submit}
                className="text-[10px] font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
              >
                {busy
                  ? t("ui.review_grouped.row_resolve_submitting")
                  : t("ui.review_grouped.row_resolve_submit")}
              </button>
            </div>
          </PopoverContent>
        </Popover>
        )}
      </div>
      {decision && n > 0 && (
        <div data-testid={`rg-bulkb-decision-${flagType}`} className="px-3 pb-3">{decision}</div>
      )}
    </div>
  );
};

export default FamilyBBulkResolver;
